import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { useState, useEffect } from "react";
import {
  Building2,
  Plus,
  Globe,
  Phone,
  MapPin,
  Briefcase,
  FileText,
  Users,
  Upload,
  Mail,
  FileCheck,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { CreateCompanyData } from "@/services/companyService";
import { industryAPI } from "@/services/industryService";
import { CreateIndustryModal } from "./CreateIndustryModal";
import { IndustryOption, CreateIndustryData } from "@/types/industry";

interface CreateCompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (company: CreateCompanyData, rtuFile: File | null) => Promise<void>;
  companySlug: string;
}

const initialForm: CreateCompanyData = {
  slug: "",
  name: "",
  logo_url: "",
  industry_id: "",
  size: "",
  phone: "",
  website: "",
  address: "",
  notes: "",
  parent_company_id: null,
  nit: "",
  email: "",
};

const SIZE_OPTIONS = [
  { value: "1-10", label: "1-10 empleados" },
  { value: "11-50", label: "11-50 empleados" },
  { value: "51-100", label: "51-100 empleados" },
  { value: "101-250", label: "101-250 empleados" },
  { value: "251-500", label: "251-500 empleados" },
  { value: "501-1000", label: "501-1000 empleados" },
  { value: "1001-5000", label: "1001-5000 empleados" },
  { value: "5001-10000", label: "Más de 5000 empleados" },
];

export function CreateCompanyModal({
  isOpen,
  onClose,
  onSave,
  companySlug,
}: CreateCompanyModalProps) {
  const { toast } = useToast();
  const [formData, setFormData] = useState<CreateCompanyData>(initialForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [industries, setIndustries] = useState<IndustryOption[]>([]);
  const [industriesLoading, setIndustriesLoading] = useState(false);
  const [isIndustryModalOpen, setIsIndustryModalOpen] = useState(false);
  const [rtuFile, setRtuFile] = useState<File | null>(null);
  const [activeTab, setActiveTab] = useState("perfil");

  useEffect(() => {
    if (isOpen && companySlug) {
      loadIndustries();
      setFormData({
        ...initialForm,
        slug: companySlug,
      });
      setErrors({});
      setRtuFile(null);
      setActiveTab("perfil");
    }
  }, [isOpen, companySlug]);

  const loadIndustries = async () => {
    try {
      setIndustriesLoading(true);
      const industryOptions = await industryAPI.getIndustryOptions(companySlug);
      setIndustries(industryOptions);
    } catch (error) {
      console.error("Error cargando industrias:", error);
      toast({
        title: "Error",
        description: "No se pudieron cargar las industrias",
        variant: "destructive",
      });
    } finally {
      setIndustriesLoading(false);
    }
  };

  const handleChange = (field: keyof CreateCompanyData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) newErrors.name = "Este campo es obligatorio";
    if (!formData.industry_id)
      newErrors.industry_id = "Selecciona una industria";

    const phoneRegex = /^[+]?[\d\s\-()]+$/;
    if (formData.phone && !phoneRegex.test(formData.phone)) {
      newErrors.phone = "Formato no válido";
    }

    const urlRegex = /^(https?:\/\/)?([\w\-]+\.)+[\w\-]+(\/[\w\- .\/?%&=]*)?$/;
    if (formData.website && !urlRegex.test(formData.website)) {
      newErrors.website = "URL no válida";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      toast({
        title: "Error de validación",
        description: "Por favor, completa los campos obligatorios",
        variant: "destructive",
      });
      if (newErrors.name || newErrors.industry_id) {
        setActiveTab("perfil");
      }
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      const companyToSave: CreateCompanyData = {
        ...formData,
        name: formData.name.trim(),
        logo_url: formData.logo_url || "",
        notes: formData.notes || "",
        parent_company_id: formData.parent_company_id || null,
        nit: formData.nit || null,
        email: formData.email || null,
      };
      
      await onSave(companyToSave, rtuFile);
      onClose();
    } catch (error: any) {
      console.error("Error en submit:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateIndustry = async (data: {
    name: string;
    is_default: boolean;
  }) => {
    try {
      const industryData: CreateIndustryData = {
        name: data.name,
        is_default: data.is_default,
        is_active: true,
        slug: companySlug,
      };

      const response = await industryAPI.createIndustry(industryData);

      if (response.success) {
        await loadIndustries();

        if (response.data?.id) {
          setFormData((prev) => ({
            ...prev,
            industry_id: response.data.id,
          }));
        }

        toast({
          title: "Industria creada",
          description: `La industria "${data.name}" ha sido creada exitosamente.`,
        });

        return true;
      }
      return false;
    } catch (error: any) {
      toast({
        title: "Error",
        description:
          error.message || "Error al crear la industria desde el modal",
        variant: "destructive",
      });
      return false;
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden bg-slate-50">
          <div className="bg-white border-b border-slate-100 p-5">
            <DialogHeader>
              <div className="flex items-center space-x-3">
                <div className="bg-blue-50 p-2.5 rounded-lg">
                  <Building2 className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <DialogTitle className="text-xl font-semibold text-slate-900">
                    Nueva Empresa Cliente
                  </DialogTitle>
                  <DialogDescription className="text-sm text-slate-500">
                    Completa la información en las pestañas correspondientes
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>
          </div>

          <form onSubmit={handleSubmit}>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <div className="px-6 bg-white border-b border-slate-100">
                <TabsList className="bg-transparent h-11 p-0 space-x-8">
                  <TabsTrigger 
                    value="perfil"
                    className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 rounded-none h-full bg-transparent px-1 font-medium"
                  >
                    Perfil Corporativo
                  </TabsTrigger>
                  <TabsTrigger 
                    value="contacto"
                    className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 rounded-none h-full bg-transparent px-1 font-medium"
                  >
                    Ubicación y Contacto
                  </TabsTrigger>
                  <TabsTrigger 
                    value="notas"
                    className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 data-[state=active]:text-blue-600 rounded-none h-full bg-transparent px-1 font-medium"
                  >
                    Notas
                  </TabsTrigger>
                </TabsList>
              </div>

              <div className="p-6 h-[300px] overflow-y-auto">
                <TabsContent value="perfil" className="mt-0 space-y-6 animate-in fade-in-50 duration-300">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2 space-y-2">
                      <Label htmlFor="name" className="text-sm font-medium text-slate-700">
                        Nombre de la Empresa <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => handleChange("name", e.target.value)}
                          placeholder="Nombre completo de la empresa"
                          className={`pl-10 border-slate-200 ${errors.name ? "border-red-500" : ""}`}
                        />
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      </div>
                      {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="industry_id" className="text-sm font-medium text-slate-700">
                        Industria <span className="text-red-500">*</span>
                      </Label>
                      <div className="flex gap-2">
                        <Select
                          value={formData.industry_id}
                          onValueChange={(value) => handleChange("industry_id", value)}
                        >
                          <SelectTrigger className="border-slate-200">
                            <SelectValue placeholder="Seleccionar industria" />
                          </SelectTrigger>
                          <SelectContent>
                            {industries.map((industry) => (
                              <SelectItem key={industry.value} value={industry.value}>
                                {industry.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => setIsIndustryModalOpen(true)}
                          className="border-blue-100 text-blue-600 hover:bg-blue-50 flex-shrink-0"
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="size" className="text-sm font-medium text-slate-700">
                        Tamaño
                      </Label>
                      <Select
                        value={formData.size}
                        onValueChange={(value) => handleChange("size", value)}
                      >
                        <SelectTrigger className="border-slate-200">
                          <SelectValue placeholder="Seleccionar tamaño" />
                        </SelectTrigger>
                        <SelectContent>
                          {SIZE_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="contacto" className="mt-0 space-y-4 animate-in fade-in-50 duration-300">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="nit" className="text-sm font-medium text-slate-700">NIT</Label>
                      <Input
                        id="nit"
                        value={formData.nit || ""}
                        onChange={(e) => handleChange("nit", e.target.value)}
                        placeholder="Identificación Tributaria"
                        className="border-slate-200"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email Corporativo</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email || ""}
                        onChange={(e) => handleChange("email", e.target.value)}
                        placeholder="correo@empresa.com"
                        className="border-slate-200"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-sm font-medium text-slate-700">Teléfono</Label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => handleChange("phone", e.target.value)}
                        placeholder="Número de contacto"
                        className="border-slate-200"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="website" className="text-sm font-medium text-slate-700">Sitio Web</Label>
                      <Input
                        id="website"
                        value={formData.website}
                        onChange={(e) => handleChange("website", e.target.value)}
                        placeholder="www.empresa.com"
                        className="border-slate-200"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="address" className="text-sm font-medium text-slate-700">Dirección de la Empresa</Label>
                      <Input
                        id="address"
                        value={formData.address}
                        onChange={(e) => handleChange("address", e.target.value)}
                        placeholder="Ubicación física fiscal"
                        className="border-slate-200"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="rtu_file" className="text-sm font-medium text-slate-700">Archivo RTU</Label>
                      <div className="flex gap-2">
                        <Input
                          id="rtu_file"
                          value={rtuFile ? rtuFile.name : ""}
                          readOnly
                          placeholder="Sin archivo"
                          className="bg-slate-100 flex-1 border-slate-200 text-xs"
                        />
                        <input
                          type="file"
                          id="rtu-upload"
                          className="hidden"
                          accept=".pdf,.png,.jpg,.jpeg"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) setRtuFile(file);
                          }}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => document.getElementById("rtu-upload")?.click()}
                          className="border-slate-200 h-9 shrink-0"
                        >
                          <Upload className="h-3 w-3 mr-1" />
                          Subir
                        </Button>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="notas" className="mt-0 animate-in fade-in-50 duration-300">
                  <div className="space-y-2">
                    <Label htmlFor="notes" className="text-sm font-medium text-slate-700">Notas Adicionales</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => handleChange("notes", e.target.value)}
                      placeholder="Información adicional relevante..."
                      className="border-slate-200 resize-none min-h-[180px]"
                    />
                  </div>
                </TabsContent>
              </div>
            </Tabs>

            <DialogFooter className="p-6 bg-white border-t border-slate-100">
              <div className="flex items-center justify-between w-full">
                <div className="text-xs text-slate-400">
                  <span className="text-red-500">*</span> Campos obligatorios
                </div>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={onClose}
                    disabled={loading}
                    className="text-slate-600"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading || !formData.name.trim() || !formData.industry_id}
                    className="bg-blue-600 hover:bg-blue-700 text-white min-w-[140px]"
                  >
                    {loading ? "Guardando..." : "Crear Empresa"}
                  </Button>
                </div>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <CreateIndustryModal
        isOpen={isIndustryModalOpen}
        onClose={() => setIsIndustryModalOpen(false)}
        onSave={handleCreateIndustry}
        companySlug={companySlug}
      />
    </>
  );
}
